import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon, UserGroupIcon, AcademicCapIcon, ShieldCheckIcon,
  UserCircleIcon, ArrowRightIcon, ClockIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';

const fmt     = n => (n||0).toLocaleString('en-PK');
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric'}) : '—';

const ROLE_COLORS = {
  admin:'#3b6ef8', teacher:'#10c98e', student:'#f59e0b',
  coordinator:'#8b5cf6', finance:'#0d9488', owner:'#ef4444', parent:'#9094a8',
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .sd * { box-sizing:border-box; }
  .sd { font-family:'Sora',sans-serif; color:#1a1d2e; animation:sdFade .4s ease both; }
  @keyframes sdFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .sd-greeting { font-size:1.5rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; margin-bottom:4px; }
  .sd-sub      { font-size:.8rem; color:#9094a8; margin-bottom:2rem; }
  .sd-stat-grid { display:grid; grid-template-columns:repeat(5,1fr); gap:1rem; margin-bottom:1.8rem; }
  .sd-stat { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem 1.3rem; position:relative; overflow:hidden; animation:sdFade .4s ease both; cursor:pointer; transition:transform .15s,box-shadow .15s; }
  .sd-stat:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(0,0,0,.08); }
  .sd-stat::after { content:''; position:absolute; top:0; right:0; width:70px; height:70px; border-radius:0 16px 0 70px; opacity:.06; }
  .sd-stat.blue::after{background:#3b6ef8}   .sd-stat.green::after{background:#10c98e}
  .sd-stat.amber::after{background:#f59e0b}  .sd-stat.purple::after{background:#8b5cf6}
  .sd-stat.red::after{background:#ef4444}
  .sd-stat-icon { width:36px; height:36px; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:.8rem; }
  .sd-stat-icon svg { width:17px; height:17px; stroke-width:1.8; }
  .sd-stat-icon.blue{background:#eef2ff;color:#3b6ef8}   .sd-stat-icon.green{background:#ecfdf5;color:#10c98e}
  .sd-stat-icon.amber{background:#fffbeb;color:#f59e0b}  .sd-stat-icon.purple{background:#f3f0ff;color:#8b5cf6}
  .sd-stat-icon.red{background:#fef2f2;color:#ef4444}
  .sd-stat-label { font-size:.68rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .sd-stat-val   { font-size:1.6rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; line-height:1.2; margin-top:2px; }
  .sd-stat-link  { font-size:.68rem; color:#3b6ef8; margin-top:6px; display:flex; align-items:center; gap:3px; }
  .sd-stat-link svg { width:11px; height:11px; stroke-width:2.5; }
  .sd-charts { display:grid; grid-template-columns:1.6fr 1fr; gap:1.2rem; margin-bottom:1.8rem; }
  .sd-chart-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.4rem; }
  .sd-chart-title { font-size:.9rem; font-weight:700; color:#0f1123; margin-bottom:.3rem; }
  .sd-chart-sub   { font-size:.72rem; color:#9094a8; margin-bottom:1.2rem; }
  .sd-recent { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.4rem; }
  .sd-recent-title { font-size:.9rem; font-weight:700; color:#0f1123; margin-bottom:1rem; display:flex; align-items:center; justify-content:space-between; }
  .sd-recent-link { font-size:.76rem; color:#3b6ef8; text-decoration:none; display:flex; align-items:center; gap:4px; cursor:pointer; }
  .sd-recent-link svg { width:12px; height:12px; stroke-width:2.5; }
  .sd-school-row { display:flex; align-items:center; gap:12px; padding:10px 0; border-bottom:1px solid #f3f5fb; }
  .sd-school-row:last-child { border-bottom:none; }
  .sd-school-avatar { width:36px; height:36px; border-radius:10px; background:linear-gradient(135deg,#3b6ef8,#8b5cf6); display:flex; align-items:center; justify-content:center; font-size:.76rem; font-weight:700; color:white; flex-shrink:0; }
  .sd-school-name { font-size:.84rem; font-weight:600; color:#0f1123; }
  .sd-school-date { font-size:.68rem; color:#9094a8; margin-top:2px; display:flex; align-items:center; gap:4px; }
  .sd-school-date svg { width:11px; height:11px; }
  .sd-loading { display:flex; align-items:center; justify-content:center; height:300px; gap:10px; color:#9094a8; }
  .sd-spin { width:20px; height:20px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg)} }
  .custom-tooltip { background:white; border:1px solid #e8ebf4; border-radius:10px; padding:10px 14px; font-family:'Sora',sans-serif; font-size:.76rem; box-shadow:0 4px 14px rgba(0,0,0,.1); }
  .custom-tooltip .label { font-weight:700; color:#0f1123; margin-bottom:4px; }
  @media (max-width:1200px) { .sd-stat-grid { grid-template-columns:repeat(3,1fr); } }
  @media (max-width:900px)  { .sd-charts { grid-template-columns:1fr; } }
  @media (max-width:600px)  { .sd-stat-grid { grid-template-columns:1fr 1fr; } }
`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="custom-tooltip">
      <div className="label">{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{color:p.color}}>{p.name}: <strong>{p.value}</strong></div>
      ))}
    </div>
  );
};

const getInitials = n => n?.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'S';

export default function SuperDashboard() {
  const navigate  = useNavigate();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/super/stats')
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load stats'))
      .finally(() => setLoading(false));
  }, []);

  const now  = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const STATS = data ? [
    { label:'Schools',  val:data.totals.schools,  icon:BuildingOfficeIcon, color:'blue',   href:'/super/schools' },
    { label:'Students', val:data.totals.students, icon:UserGroupIcon,      color:'green',  href:'/super/students' },
    { label:'Teachers', val:data.totals.teachers, icon:AcademicCapIcon,    color:'amber',  href:'/super/teachers' },
    { label:'Admins',   val:data.totals.admins,   icon:ShieldCheckIcon,    color:'purple', href:'/super/admins' },
    { label:'Owners',   val:data.totals.owners,   icon:UserCircleIcon,     color:'red',    href:'/super/owners' },
  ] : [];

  const pieData = (data?.userRoleBreakdown || []).map(r => ({
    name:  r._id?.charAt(0).toUpperCase() + r._id?.slice(1),
    value: r.count,
    color: ROLE_COLORS[r._id] || '#9094a8',
  }));

  return (
    <div className="sd"><style>{css}</style>

      <div className="sd-greeting">{greeting} 👋</div>
      <div className="sd-sub">Full system overview — all schools, users and activity across the platform.</div>

      {loading ? (
        <div className="sd-loading"><div className="sd-spin"/>Loading stats…</div>
      ) : (
        <>
          {/* Stat Cards */}
          <div className="sd-stat-grid">
            {STATS.map((s, i) => (
              <div key={s.label} className={`sd-stat ${s.color}`} style={{animationDelay:`${i*.06}s`}} onClick={() => navigate(s.href)}>
                <div className={`sd-stat-icon ${s.color}`}><s.icon /></div>
                <div className="sd-stat-label">{s.label}</div>
                <div className="sd-stat-val">{fmt(s.val)}</div>
                <div className="sd-stat-link">View all <ArrowRightIcon /></div>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="sd-charts">
            {/* Bar chart — students + teachers per school */}
            <div className="sd-chart-card">
              <div className="sd-chart-title">Students & Teachers per School</div>
              <div className="sd-chart-sub">Top {data?.schoolBreakdown?.length||0} schools by enrollment</div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data?.schoolBreakdown||[]} margin={{top:4,right:10,left:0,bottom:40}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f8" />
                  <XAxis dataKey="name" tick={{fontSize:11,fontFamily:'Sora'}} angle={-35} textAnchor="end" interval={0} />
                  <YAxis tick={{fontSize:11,fontFamily:'Sora'}} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{fontSize:12,fontFamily:'Sora',paddingTop:8}} />
                  <Bar dataKey="studentCount" name="Students" fill="#3b6ef8" radius={[4,4,0,0]} />
                  <Bar dataKey="teacherCount"  name="Teachers" fill="#10c98e" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Pie chart — users by role */}
            <div className="sd-chart-card">
              <div className="sd-chart-title">Users by Role</div>
              <div className="sd-chart-sub">Distribution across all schools</div>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="45%" outerRadius={90} innerRadius={50}
                    dataKey="value" label={({name,percent}) => `${name} ${(percent*100).toFixed(0)}%`}
                    labelLine={false} fontSize={11}>
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, n) => [fmt(v), n]} />
                  <Legend wrapperStyle={{fontSize:12,fontFamily:'Sora'}} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Recent Schools */}
          <div className="sd-recent">
            <div className="sd-recent-title">
              Recently Added Schools
              <span className="sd-recent-link" onClick={() => navigate('/super/schools')}>
                View all <ArrowRightIcon />
              </span>
            </div>
            {(data?.recentSchools||[]).length === 0 ? (
              <div style={{color:'#9094a8',fontSize:'.82rem',padding:'1rem 0'}}>No schools yet.</div>
            ) : (
              (data?.recentSchools||[]).map(s => (
                <div key={s._id} className="sd-school-row" onClick={() => navigate(`/super/schools/${s._id}`)} style={{cursor:'pointer'}}>
                  <div className="sd-school-avatar">{getInitials(s.name)}</div>
                  <div style={{flex:1}}>
                    <div className="sd-school-name">{s.name}</div>
                    <div className="sd-school-date"><ClockIcon />Added {fmtDate(s.createdAt)}</div>
                  </div>
                  <ArrowRightIcon style={{width:14,height:14,stroke:'#9094a8',strokeWidth:2.5}} />
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}